import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, AlertTriangle, BarChart3, CircleDollarSign,
  Clock3, CreditCard, Eye, EyeOff, FileSpreadsheet, Flame, RefreshCw, ScanLine, ShieldCheck, Ticket, Users
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, fmtErr } from "@/lib/api";
import { useAdminLanguage } from "@/i18n/AdminLanguage";
import AdminKndlaPromoters from "@/pages/admin/AdminKndlaPromoters";
import AdminKndlaExport from "@/pages/admin/AdminKndlaExport";
import AdminKndlaDoorSales from "@/pages/admin/AdminKndlaDoorSales";

const rub = (n) => `${Number(n || 0).toLocaleString("ru-RU")} ₽`;
const num = (n) => Number(n || 0).toLocaleString("es-ES");
const cardCls = "rounded-2xl border border-white/10 bg-ash/85 p-5";
const controlCls = "min-w-0 rounded-lg border border-white/15 bg-coal px-3 py-2.5 text-sm text-white outline-none focus:border-flame";
const stamp = (date) => date ? new Date(date).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "—";
const sum = (rows, field) => rows.reduce((total, row) => total + Number(row?.[field] || 0), 0);

function PrivacyToggle({ shown, onToggle, label }) {
  const {tr:t}=useAdminLanguage();
  const action = shown ? t("Ocultar") : t("Mostrar");
  return (
    <button type="button" onClick={onToggle}
      title={`${action} ${label}`}
      aria-label={`${action} ${label}`}
      aria-pressed={shown}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1.5 text-[11px] font-semibold text-white/75 hover:border-flame hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-flame">
      {shown ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
      <span>{action}</span>
    </button>
  );
}

function Metric({ icon: Icon, label, value, note, shown, onToggle }) {
  return (
    <div className={cardCls}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-white/50">{label}</span>
        <div className="flex shrink-0 items-center gap-2">
          <Icon size={17} className="text-flame" aria-hidden="true" />
          <PrivacyToggle shown={shown} onToggle={onToggle} label={label.toLowerCase()} />
        </div>
      </div>
      <p className="mt-4 break-words font-syne text-2xl font-bold tracking-tight text-white md:text-3xl">{shown ? value : "••••••"}</p>
      {note && <p className="mt-2 text-xs leading-relaxed text-white/40">{note}</p>}
    </div>
  );
}

function Flag({ children, status = "neutral" }) {
  const classes = status === "bad" ? "border-red-400/25 bg-red-400/10 text-red-200"
    : status === "good" ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-200"
    : "border-white/15 bg-white/5 text-white/60";
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium ${classes}`}>{children}</span>;
}

function stateLabel(event) {
  if (Number(event.hidden)) return "Retirada";
  if (event.status !== "active") return "Cerrada";
  if (event.event_date && event.event_date.slice(0, 10) < new Date().toISOString().slice(0, 10)) return "Anterior";
  return "Activa";
}

export default function AdminKndlaReport({ view = "sales" }) {
  const isSales = view === "sales";
  const {tr:t,lang}=useAdminLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [error, setError] = useState("");
  const [eventId, setEventId] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  // This selector affects ONLY the revenue chart, never the global dashboard metrics.
  const [chartEventId, setChartEventId] = useState("all");
  const [promoterId, setPromoterId] = useState("all");
  const [channel, setChannel] = useState("all");
  // Independent privacy controls; visual masking is NOT access control.
  // Counts are initially visible; monetary totals, charts and promoter details start hidden.
  const [shown, setShown] = useState({ entradas: true, escaneados: true, tarifas: true });
  const isShown = (key) => Boolean(shown[key]);
  const toggleShown = (key) => setShown((prev) => ({ ...prev, [key]: !prev[key] }));
  const money = (value, key) => value === null || value === undefined ? "—" : (isShown(key) ? rub(value) : "••••••");
  const displayCount = (value, key) => value === null || value === undefined ? "—" : (isShown(key) ? num(value) : "••••••");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/kndla/report", { timeout: 18000 });
      setReport(data);
    } catch (err) {
      setError(fmtErr(err, "No se pudo cargar el informe"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const events = useMemo(() => report?.events || [], [report]);
  const catalogEvents = useMemo(() => events.filter(e => includeArchived || !Number(e.archived)), [events, includeArchived]);
  const promoters = useMemo(() => report?.promoters || [], [report]);
  const promoterEvents = useMemo(() => report?.promoter_events || [], [report]);

  const filteredEvents = useMemo(() => events.filter((e) => eventId === "all" || String(e.id) === eventId), [events, eventId]);
  const eventSet = useMemo(() => new Set(filteredEvents.map((e) => String(e.id))), [filteredEvents]);
  const eventPromoterSales = useMemo(() => promoterEvents.filter((row) => eventSet.has(String(row.event_id))), [promoterEvents, eventSet]);
  const filteredSales = useMemo(() => eventPromoterSales.filter((row) => promoterId === "all" || String(row.promoter_id) === promoterId), [eventPromoterSales, promoterId]);
  const singlePromoter = promoterId !== "all";
  const effectiveChannel = singlePromoter ? "promoters" : channel;
  const isGlobalView = eventId === "all" && effectiveChannel === "all";

  // The existing bridge reports promoter sales and all approved orders, but does NOT
  // identify bot versus administrative/manual issuances separately. Never invent a split.
  const channelTotals = useMemo(() => {
    const all = {
      tickets: sum(filteredEvents, "tickets_sold"),
      revenue: sum(filteredEvents, "revenue_rub"),
      scanned: sum(filteredEvents, "qr_scanned"),
    };
    const promotersOnly = {
      tickets: sum(eventPromoterSales, "tickets_sold"),
      revenue: sum(eventPromoterSales, "gross_rub"),
      scanned: sum(eventPromoterSales, "qr_scanned"),
    };
    const diff = (key) => all[key] >= promotersOnly[key] ? all[key] - promotersOnly[key] : null;
    const unassigned = { tickets: diff("tickets"), revenue: diff("revenue"), scanned: diff("scanned") };
    const mismatch = Object.values(unassigned).some((x) => x === null);
    return { all, promoters: promotersOnly, unassigned, mismatch };
  }, [filteredEvents, eventPromoterSales]);

  const metrics = useMemo(() => {
    if (effectiveChannel === "all") return {
      ...channelTotals.all,
      normal: sum(filteredEvents, "regular_tickets"),
      group: sum(filteredEvents, "group_tickets"),
      unknown: sum(filteredEvents, "unknown_tickets"),
      debt: sum(eventPromoterSales, "owed_rub"),
    };
    if (effectiveChannel === "unassigned") return {
      ...channelTotals.unassigned, normal: null, group: null, unknown: null, debt: null,
    };
    return {
      tickets: sum(filteredSales, "tickets_sold"),
      revenue: sum(filteredSales, "gross_rub"),
      scanned: sum(filteredSales, "qr_scanned"),
      normal: null, group: null, unknown: null,
      debt: sum(filteredSales, "owed_rub"),
    };
  }, [effectiveChannel, channelTotals, filteredEvents, eventPromoterSales, filteredSales]);

  const promoterRows = useMemo(() => promoters.filter((p) => promoterId === "all" || String(p.telegram_id) === promoterId).map((p) => {
    if (eventId === "all") return { ...p, requests_pending: sum(promoterEvents.filter((s) => String(s.promoter_id) === String(p.telegram_id)), "requests_pending") };
    const row = promoterEvents.find((s) => String(s.promoter_id) === String(p.telegram_id) && String(s.event_id) === eventId);
    return {
      ...p, sales: row?.sales || 0, tickets_sold: row?.tickets_sold || 0,
      gross_rub: row?.gross_rub || 0, commission_rub: row?.commission_rub || 0,
      transferred_rub: row?.transferred_rub || 0, owed_rub: row?.owed_rub || 0,
      tickets_pending_event: row?.tickets_pending || 0,
      qr_scanned: row?.qr_scanned || 0, requests_pending: row?.requests_pending || 0,
    };
  }), [promoters, promoterEvents, eventId, promoterId]);

  // The chart deliberately uses the unfiltered event list from D1.
  // It is NOT affected by eventId, channel, promoterId or their metrics.
  const chartEvents = useMemo(() => events.filter((e) =>
    chartEventId === "all" ||
    (chartEventId === "active" && stateLabel(e) === "Activa") ||
    String(e.id) === chartEventId
  ), [events, chartEventId]);

  const chartData = useMemo(() => chartEvents.map((e) => ({
    name: e.name.length > 21 ? `${e.name.slice(0, 20)}…` : e.name,
    fullName: e.name,
    date: e.event_date || "",
    ingresos: Number(e.revenue_rub || 0),
  })).filter((e) => Number.isFinite(e.ingresos) && e.ingresos > 0)
    .sort((a, b) => a.date.localeCompare(b.date)), [chartEvents]);

  const chartLabels = {
    es: { filter: "Fiestas de esta gráfica", all: "Todas las fiestas", active: "Solo fiestas activas", note: "Este filtro solo afecta a la gráfica. Los totales y las demás tablas no cambian." },
    en: { filter: "Parties in this chart", all: "All parties", active: "Active parties only", note: "This filter only affects the chart. Totals and other tables will not change." },
    ru: { filter: "Вечеринки на этом графике", all: "Все вечеринки", active: "Только активные вечеринки", note: "Этот фильтр влияет только на график. Итоги и другие таблицы не меняются." },
  }[lang] || { filter: "Fiestas de esta gráfica", all: "Todas las fiestas", active: "Solo fiestas activas", note: "Este filtro solo afecta a la gráfica. Los totales y las demás tablas no cambian." };

  const chooseChannel = (value) => {
    setChannel(value);
    if (value !== "promoters") setPromoterId("all");
  };
  const choosePromoter = (value) => {
    setPromoterId(value);
    if (value !== "all") setChannel("promoters");
  };

  return (
    <section data-testid="kndla-report" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-flame"><Flame size={20} /><span className="text-[10px] font-bold uppercase tracking-[0.2em]">KNDLA · Cloudflare D1</span></div>
          <h2 className="mt-2 font-syne text-2xl font-extrabold text-white">{isSales ? t('Sales') : t('Promoters')}</h2>
          <p className="mt-1 text-xs text-white/45">{isSales ? t('Información del bot oficial. Consulta protegida y solo lectura.') : t('Administración de promotores y estadísticas de Cloudflare D1.')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={loading} onClick={load}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white hover:border-flame disabled:opacity-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t("Actualizar datos")}
          </button>
          {isSales && report && <button type="button"
            onClick={() => setExportOpen((value) => !value)}
            aria-expanded={exportOpen}
            aria-controls="kndla-excel-panel"
            className="inline-flex items-center gap-2 rounded-full border border-flame/40 px-4 py-2 text-xs font-bold text-flame hover:bg-flame/10">
            <FileSpreadsheet size={15} aria-hidden="true" />
            {({es: "Exportar a Excel", en: "Export to Excel", ru: "Экспорт в Excel"}[lang] || "Exportar a Excel")}
          </button>}
        </div>
      </div>
      {isSales && report && exportOpen && (
        <div id="kndla-excel-panel">
          <AdminKndlaExport events={events} />
        </div>
      )}

      {!isSales && <AdminKndlaPromoters onChanged={load} report={report} />}


      {error && <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">
        <AlertTriangle size={17} className="mt-0.5 shrink-0" /> <div><b>{t('No se pudo cargar la información.')}</b><p className="mt-1 text-xs">{error}</p>{report && <p className="mt-1 text-xs">{t('Se muestra la última consulta disponible.')}</p>}</div>
      </div>}
      {loading && !report && <div className={`${cardCls} py-14 text-center text-sm text-white/50`}>{t('Consultando las ventas reales de KNDLA…')}</div>}

      {report && <>
        {/* El orden visible de Ventas es: gráfica, puerta, tarifas, filtros+KPIs, canales y fiestas. */}
        {isSales && (
        <div className={cardCls}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><BarChart3 className="text-flame" size={17} /><h3 className="font-syne font-bold">{t('Ingresos por fiesta')}</h3></div>
            <PrivacyToggle shown={isShown("grafico")} onToggle={() => toggleShown("grafico")} label={t("gráfico de ingresos")} />
          </div>
          <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-white/50" htmlFor="kndla-chart-event-filter">{chartLabels.filter}</label>
          <select id="kndla-chart-event-filter" className={`${controlCls} mb-2 w-full`} value={chartEventId} onChange={(e) => setChartEventId(e.target.value)}>
            <option value="all">{chartLabels.all}</option>
            <option value="active">{chartLabels.active}</option>
            {events.map((e) => <option key={e.id} value={String(e.id)}>{e.name} · {e.event_date?.slice(0, 10) || "—"}{Number(e.hidden) ? " (retirada)" : ""}</option>)}
          </select>
          <p className="mb-5 text-xs leading-relaxed text-white/40">{chartLabels.note}</p>
          {!isShown("grafico") ? <div className="flex h-[245px] flex-col items-center justify-center gap-3 text-sm text-white/45"><EyeOff size={24} aria-hidden="true"/><span>{t('Gráfico oculto para proteger los importes.')}</span></div> : chartData.length ? <div className="h-[260px] w-full overflow-x-auto" role="img" aria-label={t("Gráfico de ingresos aprobados por fiesta en rublos")}>
            <div className="h-[245px]" style={{ width: chartData.length > 7 ? `${Math.max(720, chartData.length * 96)}px` : "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 32, left: 6 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#aaa", fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={64} />
                  <YAxis width={52} tick={{ fill: "#aaa", fontSize: 10 }} tickFormatter={(n) => `${Math.round(n / 1000)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: "#1a1a1a", border: "1px solid #444", borderRadius: 12 }} formatter={(v) => [rub(v), t("Ingresos")]} labelFormatter={(_, items) => items?.[0]?.payload?.fullName || ""} />
                  <Bar dataKey="ingresos" fill="#f97316" radius={[4, 4, 0, 0]} maxBarSize={45} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div> : <div className="flex h-[245px] items-center justify-center text-sm text-white/35">{t('Aún no hay ventas aprobadas en este filtro.')}</div>}
        </div>
)}

        {isSales && <AdminKndlaDoorSales events={events} />}

        {isSales && (
        <div className={cardCls}>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><CreditCard size={17} className="text-flame" /><h3 className="font-syne font-bold">{t('Entradas por tipo de tarifa')}</h3></div>
            <PrivacyToggle shown={isShown("tarifas")} onToggle={() => toggleShown("tarifas")} label={t("datos de tarifas")} />
          </div>
          {effectiveChannel !== "all" ? <p className="text-sm leading-6 text-white/55">{t('El reporte no permite dividir las tarifas por canal o promotor. Selecciona «Todos los canales» para ver el desglose sin atribuir tarifas a vendedores incorrectos.')}</p> : <div className="space-y-5">
            <div><p className="text-xs text-white/45">{t('Tarifa normal')}</p><p className="mt-1 font-syne text-2xl font-bold">{displayCount(metrics.normal, "tarifas")}</p></div>
            <div><p className="text-xs text-white/45">{t('Promoción grupal')}</p><p className="mt-1 font-syne text-2xl font-bold">{displayCount(metrics.group, "tarifas")}</p></div>
            <div><p className="text-xs text-white/45">{t('Sin clasificación')}</p><p className="mt-1 font-syne text-xl font-semibold">{displayCount(metrics.unknown, "tarifas")}</p></div>
            <p className="text-xs leading-5 text-white/40">{t('Las compras anteriores sin registro de tarifa pueden estar clasificadas por inferencia. Si cambiaste los precios, comprueba su historial.')}</p>
          </div>}
          {eventId !== "all" && (() => {
            const event = events.find((e) => String(e.id) === eventId);
            return event && <div className="mt-5 border-t border-white/10 pt-4 text-sm text-white/60">
              <p>{t("Precio normal:")} <b className="text-white">{money(event.price_rub, "tarifas")}</b></p>
              <p className="mt-1">{t("Promoción:")} <b className="text-white">{event.promo_min_quantity ? (isShown("tarifas") ? `${event.promo_min_quantity}+ a ${rub(event.promo_price_rub)} c/u` : `${event.promo_min_quantity}+ a •••••• c/u`) : t("Sin promoción")}</b></p>
            </div>;
          })()}
        </div>
)}

        <div className={`${cardCls} grid gap-4 sm:grid-cols-2 lg:grid-cols-3`}>
          <label className="space-y-2"><span className="block text-[11px] font-bold uppercase tracking-wider text-white/50">{t('Filtrar por fiesta')}</span>
            <select className={`${controlCls} w-full`} value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="all">{t('Todas las fiestas')}</option>
              {catalogEvents.map((e) => <option key={e.id} value={String(e.id)}>{e.name} · {e.event_date?.slice(0, 10) || "Sin fecha"}{Number(e.archived) ? " (archivada)" : Number(e.hidden) ? " (retirada)" : ""}</option>)}
            </select>
            <label className="mt-2 flex items-center gap-2 text-[11px] text-white/50">
              <input type="checkbox" checked={includeArchived} onChange={e => {setIncludeArchived(e.target.checked); if (!e.target.checked && Number(events.find(x=>String(x.id)===eventId)?.archived)) setEventId("all");}} />
              {{es:"Incluir fiestas archivadas en este selector",en:"Include archived parties in this selector",ru:"Показать архивные вечеринки в списке"}[lang] || "Incluir fiestas archivadas en este selector"}
            </label>
          </label>
          {isSales && <label className="space-y-2"><span className="block text-[11px] font-bold uppercase tracking-wider text-white/50">{t('Canal de venta')}</span>
            <select className={`${controlCls} w-full`} value={effectiveChannel} onChange={(e) => chooseChannel(e.target.value)}>
              <option value="all">{t('Todos los canales')}</option>
              <option value="promoters">{t('Promotores')}</option>
              <option value="unassigned">{t('Sin promotor (bot / admin)')}</option>
            </select>
          </label>}
          <label className="space-y-2"><span className="block text-[11px] font-bold uppercase tracking-wider text-white/50">{t('Filtrar por promotor')}</span>
            <select className={`${controlCls} w-full`} value={promoterId} onChange={(e) => choosePromoter(e.target.value)}>
              <option value="all">{t('Todos los promotores')}</option>
              {promoters.map((p) => <option key={p.telegram_id} value={String(p.telegram_id)}>{p.display_name}{Number(p.active) ? "" : " (suspendido)"}</option>)}
            </select>
          </label>
          <p className="text-xs text-white/40 sm:col-span-2 lg:col-span-3">{!isSales ? t("Estadísticas de promotores para la fiesta y el promotor seleccionados.") : isGlobalView ? t("Resumen de todos los eventos, incluidos los retirados.") : singlePromoter ? t("Se muestran únicamente las ventas del promotor elegido.") : effectiveChannel === "unassigned" ? t("Ventas sin promotor asociado: pueden incluir compras del bot y emisiones administrativas.") : t("Vista filtrada según la fiesta y el canal seleccionados.")} {t("Última actualización:")} {stamp(report.generated_at)}.</p>
        </div>

        {isSales && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Metric icon={CircleDollarSign} label={t("Ingresos registrados")} value={metrics.revenue === null ? "—" : rub(metrics.revenue)} shown={isShown("ingresos")} onToggle={() => toggleShown("ingresos")} note={t("Pedidos aprobados; no equivale a saldo bancario")} />
          <Metric icon={Ticket} label={t("Entradas vendidas")} value={metrics.tickets === null ? "—" : num(metrics.tickets)} shown={isShown("entradas")} onToggle={() => toggleShown("entradas")} note={t("Entradas de ventas registradas")} />
          <Metric icon={ScanLine} label={t("QR escaneados")} value={metrics.scanned === null ? "—" : num(metrics.scanned)} shown={isShown("escaneados")} onToggle={() => toggleShown("escaneados")} note={t("Accesos validados en eventos")} />
        </div>}
        {!isSales && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Metric icon={Clock3} label={t("Pendiente de promotores")} value={rub(sum(filteredSales, "owed_rub"))} shown={isShown("pendiente")} onToggle={() => toggleShown("pendiente")} note={t("Neto debido a KNDLA, no liquidado")} />
        </div>}

        {isSales && promoterId === "all" && <div className={cardCls}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2"><Users size={18} className="text-flame" /><h3 className="font-syne text-lg font-bold">{t('Canales de venta')}</h3></div>
            <PrivacyToggle shown={isShown("canales")} onToggle={() => toggleShown("canales")} label={t("ingresos por canal")} />
          </div>
          <p className="mb-4 text-xs leading-5 text-white/45">{t('Desglose por fiesta seleccionada. Las ventas sin promotor pueden venir del bot oficial o de una emisión administrativa: el reporte actual no permite distinguirlas con seguridad.')}</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead><tr className="border-b border-white/10 text-xs text-white/45"><th className="pb-3 font-medium">{t('Origen')}</th><th className="pb-3 text-right font-medium">{t('Entradas')}</th><th className="pb-3 text-right font-medium">{t('Ingresos')}</th><th className="pb-3 text-right font-medium">{t('QR escaneados')}</th></tr></thead>
              <tbody>
                <tr className="border-b border-white/10"><td className="py-3 pr-4">{t('👥 Promotores')}</td><td className="py-3 text-right">{num(channelTotals.promoters.tickets)}</td><td className="py-3 text-right">{money(channelTotals.promoters.revenue, "canales")}</td><td className="py-3 text-right">{num(channelTotals.promoters.scanned)}</td></tr>
                <tr className="border-b border-white/10"><td className="py-3 pr-4">{t("🤖 Bot / 🛡️ Administración")} <span className="block text-xs text-white/40">{t('Sin promotor identificado')}</span></td><td className="py-3 text-right">{channelTotals.unassigned.tickets === null ? "—" : num(channelTotals.unassigned.tickets)}</td><td className="py-3 text-right">{money(channelTotals.unassigned.revenue, "canales")}</td><td className="py-3 text-right">{channelTotals.unassigned.scanned === null ? "—" : num(channelTotals.unassigned.scanned)}</td></tr>
                <tr className="font-semibold text-white"><td className="py-3 pr-4">{t('Total de pedidos aprobados')}</td><td className="py-3 text-right">{num(channelTotals.all.tickets)}</td><td className="py-3 text-right">{money(channelTotals.all.revenue, "canales")}</td><td className="py-3 text-right">{num(channelTotals.all.scanned)}</td></tr>
              </tbody>
            </table>
          </div>
          {channelTotals.mismatch && <p role="alert" className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-200">{t('Las cifras atribuidas a promotores superan el total aprobado en al menos un indicador. No se muestra una diferencia potencialmente incorrecta. Revisa las ventas históricas antes de conciliar.')}</p>}
          <p className="mt-3 text-xs leading-5 text-white/40">{t('«Sin promotor» se calcula restando las ventas atribuidas a promotores del total aprobado. No sustituye una clasificación individual del origen de cada pedido ni una conciliación bancaria.')}</p>
        </div>}


        {!isSales && <div className={cardCls}>
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div><div className="flex items-center gap-2"><Users size={18} className="text-flame" /><h3 className="font-syne text-lg font-bold">{t('Promotores')}</h3></div><p className="mt-1 text-xs text-white/40">{t('Cada promotor tiene un control privado independiente. El límite de entradas sigue siendo global.')}</p></div>
            <Flag><ShieldCheck size={12} className="mr-1" /> {t("Estadísticas privadas")}</Flag>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-left text-xs">
              <thead><tr className="border-b border-white/10 text-white/45">
                <th className="pb-3 pr-4 font-medium">{t('Promotor / estado')}</th><th className="pb-3 pr-4 font-medium">{t('Entradas')}</th>
                <th className="pb-3 pr-4 font-medium">{t('Ingresos')}</th><th className="pb-3 pr-4 font-medium">{t('Comisión')}</th>
                <th className="pb-3 pr-4 font-medium">{t('Transferido')}</th><th className="pb-3 pr-4 font-medium">{t('Pendiente')}</th>
                <th className="pb-3 pr-4 font-medium">{t('Límite global')}</th><th className="pb-3 font-medium">{t('Solicitudes')}</th>
              </tr></thead>
              <tbody>{promoterRows.map((p) => <tr key={p.telegram_id} className="border-b border-white/5 last:border-0">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-white">{p.display_name}</span>
                    <PrivacyToggle shown={isShown(`promotor:${p.telegram_id}`)} onToggle={() => toggleShown(`promotor:${p.telegram_id}`)} label={`datos financieros de ${p.display_name}`} />
                  </div>
                  <span className="mt-1 block text-[10px] text-white/35">{Number(p.active) ? "Activo" : "Suspendido"} · {isShown(`promotor:${p.telegram_id}`) ? `${Number(p.commission_bps) / 100}% comisión` : t("comisión oculta")}</span>
                </td>
                <td className="py-3 pr-4">{num(p.tickets_sold)}</td><td className="py-3 pr-4">{money(p.gross_rub, `promotor:${p.telegram_id}`)}</td>
                <td className="py-3 pr-4">{money(p.commission_rub, `promotor:${p.telegram_id}`)}</td><td className="py-3 pr-4">{money(p.transferred_rub, `promotor:${p.telegram_id}`)}</td>
                <td className="py-3 pr-4 font-semibold text-white">{money(p.owed_rub, `promotor:${p.telegram_id}`)}</td>
                <td className="py-3 pr-4"><Flag status={Number(p.tickets_pending) >= Number(p.ticket_limit) ? "bad" : "neutral"}>{num(p.tickets_pending)} / {num(p.ticket_limit)}</Flag></td>
                <td className="py-3">{Number(p.requests_pending) ? <Flag status="bad">{num(p.requests_pending)} pendiente(s)</Flag> : "—"}</td>
              </tr>)}</tbody>
            </table>
            {!promoterRows.length && <p className="py-8 text-center text-sm text-white/45">{t('No hay promotores registrados.')}</p>}
          </div>
          <p className="mt-4 text-xs text-white/40"><AlertTriangle size={12} className="mr-1 inline" /> {t("La tabla es de solo lectura. Utiliza «Administrar promotores» para modificar comisiones, suspender promotores y revisar transferencias.")}</p>
        </div>}

        {isSales && <div className={cardCls}>
          <div className="mb-4 flex items-center gap-2"><Activity size={18} className="text-flame" /><h3 className="font-syne text-lg font-bold">{t('Fiestas y entradas')}</h3></div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredEvents.map((e) => {
              const byEvent = eventPromoterSales.filter((r) => String(r.event_id) === String(e.id));
              const selected = filteredSales.filter((r) => String(r.event_id) === String(e.id));
              const promoterTickets = sum(byEvent, "tickets_sold");
              const promoterGross = sum(byEvent, "gross_rub");
              const promoterScans = sum(byEvent, "qr_scanned");
              const channelDifference = (total, known) => Number(total || 0) >= known ? Number(total || 0) - known : null;
              const tickets = effectiveChannel === "all" ? e.tickets_sold : effectiveChannel === "unassigned" ? channelDifference(e.tickets_sold, promoterTickets) : sum(selected, "tickets_sold");
              const gross = effectiveChannel === "all" ? e.revenue_rub : effectiveChannel === "unassigned" ? channelDifference(e.revenue_rub, promoterGross) : sum(selected, "gross_rub");
              const scans = effectiveChannel === "all" ? e.qr_scanned : effectiveChannel === "unassigned" ? channelDifference(e.qr_scanned, promoterScans) : sum(selected, "qr_scanned");
              const privacyKey = `fiesta:${e.id}`;
              return <div key={e.id} className="rounded-xl border border-white/10 bg-coal/60 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="font-semibold text-white">{e.name}</p>
                  <div className="flex items-center gap-2">
                    <Flag status={stateLabel(e) === "Activa" ? "good" : "neutral"}>{Number(e.archived) ? ({es:"Archivada",en:"Archived",ru:"В архиве"}[lang] || "Archivada") : t(stateLabel(e))}</Flag>
                    <PrivacyToggle shown={isShown(privacyKey)} onToggle={() => toggleShown(privacyKey)} label={`importes de ${e.name}`} />
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-white/40">{e.event_date || "Fecha sin definir"}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div><p className="text-white/45">{t('Entradas')}</p><b className="mt-1 block text-sm text-white">{tickets === null ? "—" : num(tickets)}</b></div>
                  <div><p className="text-white/45">{t('Ingresos')}</p><b className="mt-1 block text-sm text-white">{money(gross, privacyKey)}</b></div>
                  <div><p className="text-white/45">{t('QR escaneados')}</p><b className="mt-1 block text-sm text-white">{scans === null ? "—" : num(scans)}</b></div>
                  <div><p className="text-white/45">{t('Tarifa regular')}</p><b className="mt-1 block text-sm text-white">{money(e.price_rub, privacyKey)}</b></div>
                </div>
                <div className="mt-4 border-t border-white/10 pt-3 text-xs text-white/60">{e.promo_min_quantity ? `Promoción: ${e.promo_min_quantity}+ entradas a ${money(e.promo_price_rub, privacyKey)} c/u` : t("Sin promoción grupal")}</div>
              </div>;
            })}
          </div>
          {!filteredEvents.length && <p className="py-5 text-sm text-white/45">{t('No hay fiestas para mostrar.')}</p>}
        </div>}
        <p className="text-xs leading-5 text-white/35">{t("Fuente: pedidos aprobados y tickets de Cloudflare D1. Cada control oculta datos únicamente en pantalla y funciona de forma independiente; el acceso real está protegido por la autenticación administrativa. El desglose bot/administración requiere ampliar el reporte privado para identificar el origen de cada pedido. Los importes no equivalen a conciliación con Sberbank.")}</p>
      </>}
    </section>
  );
}
